// docs/131 the collection log, as the client lays it out: every catalogue piece but the relic
// templates gets one square (CL3), on pages grouped as docs/181 groups the hero kit (CL4). On a
// family page each family is a block of a fixed width that wraps, commons first, legendaries last;
// the Sets page gives each set a row of its own, and set pieces appear only there; the Basics page
// holds the staples every counter keeps (docs/159 SB6), and they appear only there. The layout is
// worked out from the catalogue, never stored, so adding pieces only moves squares around.
// A square is unknown, hinted (one of its eight neighbours is collected; on the Sets page, another
// piece of its set) or collected (CL5), and a
// hinted or collected piece says where it is found (CL6) from the `found`/`sold` keys the build
// wrote from the module's rules.
import { catalogueItems, findLevels, itemSets, FAMILIES, type CatalogueItem, type Family, type FindLevels, type ItemSetSpec } from './catalogue';

export type PageId =
  | 'basics' | 'main_hand' | 'off_hand' | 'head' | 'body' | 'back' | 'hands' | 'waist' | 'legs' | 'feet'
  | 'trinkets' | 'draughts' | 'bombs' | 'provisions' | 'treasure' | 'sets';

/** CL4: the pages in order, each with the families it holds (the Sets page holds sets instead). */
export const PAGES: ReadonlyArray<{ id: PageId; title: string; families: readonly Family[] }> = [
  // docs/159 SB6: the staples, by family, and nothing else
  { id: 'basics', title: 'The basics', families: ['potion', 'oil', 'scroll', 'bomb', 'food', 'tool', 'curio'] },
  { id: 'main_hand', title: 'Main hand', families: ['sword', 'dagger', 'axe', 'mace', 'spear', 'staff', 'wand', 'bow', 'crossbow'] },
  { id: 'off_hand', title: 'Off hand', families: ['shield', 'focus'] },
  { id: 'head', title: 'Head', families: ['helmet'] },
  { id: 'body', title: 'Body', families: ['armour'] },
  { id: 'back', title: 'Back', families: ['cape'] },
  { id: 'hands', title: 'Hands', families: ['gloves'] },
  { id: 'waist', title: 'Waist', families: ['belt'] },
  { id: 'legs', title: 'Legs', families: ['legs'] },
  { id: 'feet', title: 'Feet', families: ['boots'] },
  { id: 'trinkets', title: 'Rings & charms', families: ['ring', 'charm', 'neck'] },
  { id: 'draughts', title: 'Potions & oils', families: ['potion', 'oil'] },
  { id: 'bombs', title: 'Bombs & scrolls', families: ['bomb', 'scroll'] },
  { id: 'provisions', title: 'Tools, food & tomes', families: ['tool', 'food', 'tome'] },
  { id: 'treasure', title: 'Treasure & curios', families: ['treasure', 'curio'] },
  { id: 'sets', title: 'Sets', families: [] },
];

/** How many squares wide a family block is (fixed, so a square's neighbours never depend on the screen). */
export const BLOCK_WIDTH = 10;

const FAMILY_TITLES: Partial<Record<Family, string>> = {
  sword: 'Swords', dagger: 'Daggers', axe: 'Axes', mace: 'Maces', spear: 'Spears', staff: 'Staves', wand: 'Wands',
  bow: 'Bows', crossbow: 'Crossbows', shield: 'Shields', focus: 'Foci', helmet: 'Helmets', armour: 'Armour',
  cape: 'Capes', gloves: 'Gloves', belt: 'Belts', legs: 'Legs', boots: 'Boots', ring: 'Rings', charm: 'Charms',
  neck: 'Neckpieces', potion: 'Potions', oil: 'Oils', bomb: 'Bombs', scroll: 'Scrolls', tool: 'Tools',
  food: 'Food', tome: 'Tomes', treasure: 'Treasure', curio: 'Curios',
};

/** One block of squares: a family, or a set. `rows[r][c]` is a def, or `null` past the end of a row. */
export interface Block {
  key: string;
  title: string;
  /** The set's id, on the Sets page. */
  set?: number;
  width: number;
  rows: ReadonlyArray<ReadonlyArray<number | null>>;
}

export interface Page {
  id: PageId;
  title: string;
  /** A family page's blocks, one per family; the Sets page's one block, a row per set. */
  blocks: readonly Block[];
  /** Every def on the page, in reading order. */
  defs: readonly number[];
}

/** Where a square is. */
export interface Square { def: number; page: PageId; block: string; row: number; col: number }

export interface Layout {
  pages: readonly Page[];
  squares: ReadonlyMap<number, Square>;
  /** Each square's neighbours (up to eight; three or five at an edge or corner); a set piece's are the rest of its set. */
  neighbours: ReadonlyMap<number, readonly number[]>;
  /** How many squares there are (CL8's "of N"). */
  total: number;
}

/** CL3: whether a piece has a square (every catalogue piece but a relic template). */
export const onTheGrid = (it: CatalogueItem): boolean => !it.relic;

/** docs/159 SB6: a staple, kept on the Basics page alone. */
export const isStaple = (it: CatalogueItem): boolean => !!it.staple?.length;

const familyRank = (f: Family): number => FAMILIES.indexOf(f);
const byRarityThenId = (a: CatalogueItem, b: CatalogueItem): number => a.rarity - b.rarity || a.id - b.id;

function wrap(defs: readonly number[], width: number): (number | null)[][] {
  const rows: (number | null)[][] = [];
  for (let i = 0; i < defs.length; i += width) {
    const row: (number | null)[] = defs.slice(i, i + width);
    while (row.length < width) row.push(null);
    rows.push(row);
  }
  return rows;
}

/** CL4: the whole log, from these pieces and sets (the catalogue's by default). */
export function collectionLayout(items: readonly CatalogueItem[] = catalogueItems(), sets: readonly ItemSetSpec[] = itemSets()): Layout {
  const pieces = items.filter(onTheGrid);
  const byId = new Map(pieces.map((it) => [it.id, it]));
  const inSet = new Set<number>();
  const setRows: { set: ItemSetSpec; defs: number[] }[] = [];
  for (const s of [...sets].sort((a, b) => a.id - b.id)) {
    const members = s.members.map((m) => byId.get(m)).filter((x): x is CatalogueItem => !!x);
    if (!members.length) continue;
    members.sort((a, b) => familyRank(a.family) - familyRank(b.family) || a.id - b.id);
    for (const m of members) inSet.add(m.id);
    setRows.push({ set: s, defs: members.map((m) => m.id) });
  }
  const pages: Page[] = [];
  for (const p of PAGES) {
    if (p.id === 'sets') {
      const width = Math.max(1, ...setRows.map((r) => r.defs.length));
      const rows = setRows.map((r) => [...r.defs, ...Array<null>(width - r.defs.length).fill(null)]);
      const blocks: Block[] = setRows.length ? [{ key: 'sets', title: 'Sets', width, rows }] : [];
      pages.push({ id: p.id, title: p.title, blocks, defs: setRows.flatMap((r) => r.defs) });
      continue;
    }
    const blocks: Block[] = [];
    for (const f of p.families) {
      const defs = pieces.filter((it) => it.family === f && !inSet.has(it.id) && isStaple(it) === (p.id === 'basics')).sort(byRarityThenId).map((it) => it.id);
      if (defs.length) blocks.push({ key: f, title: FAMILY_TITLES[f] ?? f, width: BLOCK_WIDTH, rows: wrap(defs, BLOCK_WIDTH) });
    }
    pages.push({ id: p.id, title: p.title, blocks, defs: blocks.flatMap((b) => b.rows.flat().filter((d): d is number => d !== null)) });
  }
  const squares = new Map<number, Square>();
  const neighbours = new Map<number, number[]>();
  for (const page of pages) {
    for (const b of page.blocks) {
      b.rows.forEach((row, r) => row.forEach((def, c) => { if (def !== null) squares.set(def, { def, page: page.id, block: b.key, row: r, col: c }); }));
      b.rows.forEach((row, r) => row.forEach((def, c) => {
        if (def === null) return;
        // on the Sets page a piece hints the rest of its set, wherever they sit, and nothing else
        if (page.id === 'sets') { neighbours.set(def, row.filter((d): d is number => d !== null && d !== def)); return; }
        const out: number[] = [];
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            if (!dr && !dc) continue;
            const n = b.rows[r + dr]?.[c + dc];
            if (n !== undefined && n !== null) out.push(n);
          }
        }
        neighbours.set(def, out);
      }));
    }
  }
  return { pages, squares, neighbours, total: squares.size };
}

let cached: Layout | undefined;
/** The catalogue's layout, worked out once. */
export function theLayout(): Layout {
  cached ??= collectionLayout();
  return cached;
}

export type SquareState = 'unknown' | 'hinted' | 'collected';

/** CL5: a square is collected, hinted (a neighbour of it, or on the Sets page a piece of its set, is collected) or unknown. */
export function squareState(layout: Layout, def: number, collected: ReadonlySet<number>): SquareState {
  if (collected.has(def)) return 'collected';
  return (layout.neighbours.get(def) ?? []).some((n) => collected.has(n)) ? 'hinted' : 'unknown';
}

/** CL8: how many of these defs are collected. */
export const countCollected = (defs: readonly number[], collected: ReadonlySet<number>): number => defs.reduce((n, d) => n + (collected.has(d) ? 1 : 0), 0);

// ---- CL6: where to find a piece ----------------------------------------------------------------

/** The adventure kinds a `found` key names, in the module's order. */
const ADVENTURE_WORDS: Record<string, string> = {
  combat: 'Combat', defense: 'Defense', escort: 'Escort', retrieval: 'Retrieval', investigation: 'Investigation',
  expedition: 'Expedition', puzzle: 'Puzzle', negotiation: 'Negotiation',
};
const STASH_WORDS: Record<string, string> = {
  war_chest: 'war chests',
  cache: 'hidden caches',
  loose_chest: 'chests in fights outside an adventure',
};
const KEEPER_WORDS: Record<string, string> = { smithy: 'the smithy', shop: 'the shop', inn: 'the inn', temple: 'the temple' };

/** "a", "a and b", "a, b and c". */
export function andList(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/** The keys the build wrote for a piece (docs/131 CL6). */
type Sourced = Pick<CatalogueItem, 'rarity'> & { found?: readonly string[]; sold?: readonly string[]; staple?: readonly string[] };

/** " (from level 8)" when a source gives a piece of this rarity only from a level past the first. */
function fromLevel(levels: FindLevels, key: string, rarity: number): string {
  const l = levels[key]?.[rarity - 1];
  return l && l > 1 ? ` (from level ${l})` : '';
}

/**
 * CL6: "Found on Expedition adventures · Sold at the smithy", from the piece's `found` and `sold`;
 * CL10: a source that gives the piece's rarity only from some level says so, "in war chests (from
 * level 8)". Adventures of different kinds share one level.
 */
export function whereToFind(it: Sourced, levels: FindLevels = findLevels()): string {
  const found = it.found ?? [];
  const kinds = found.filter((k) => k in ADVENTURE_WORDS);
  const stashes = found.filter((k) => k in STASH_WORDS);
  const parts: string[] = [];
  const on = kinds.length ? `on ${andList(kinds.map((k) => ADVENTURE_WORDS[k]!))} adventures${fromLevel(levels, kinds[0]!, it.rarity)}` : '';
  const inn = stashes.length ? `in ${andList(stashes.map((k) => `${STASH_WORDS[k]!}${fromLevel(levels, k, it.rarity)}`))}` : '';
  if (on || inn) parts.push(`Found ${[on, inn].filter(Boolean).join(' and ')}`);
  const sold = (it.sold ?? []).map((k) => KEEPER_WORDS[k] ?? k);
  // a rare piece is stocked only in a village or a town (`stock_rarity_weight`)
  const where = it.rarity >= 3 ? ' of a village or town' : '';
  // docs/159 SB3: a staple never sells out where it is stocked
  if (it.staple?.length) {
    const shelves = [...(it.staple.includes('market') ? ['the founding market'] : []), ...sold];
    if (shelves.length) parts.push(`Always in stock at ${andList(shelves)}${sold.length ? where : ''}`);
  } else if (sold.length) parts.push(`Sold at ${andList(sold)}${where}`);
  return parts.length ? parts.join(' · ') : 'Not found or sold anywhere yet';
}
